import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  Plane,
  Users,
  AlertTriangle,
  FileText,
  Receipt,
  MessageSquare,
  Edit2,
  Calendar,
  CheckCircle2,
} from 'lucide-react';
import type { ClaimState } from '../types';
import { currencies, incidentTypes, type ClaimTheme } from '../constants';
import { AnimatedButton } from '../shared/AnimatedButton';
import { cn } from '@/lib/utils';

interface ReviewSectionProps {
  state: ClaimState;
  onSubmit: () => void;
  onBack: () => void;
  onEditSection: (section: string) => void;
  theme?: ClaimTheme;
}

export function ReviewSection({
  state,
  onSubmit,
  onBack,
  onEditSection,
  theme = 'invol',
}: ReviewSectionProps) {
  const expenseTotal = state.expenses.reduce((sum, e) => sum + e.amount_cents, 0);
  const currencySymbol =
    currencies.find((c) => c.value === (state.expenses[0]?.currency || 'EUR'))?.symbol || '\u20AC';

  const canSubmit =
    state.editableSegments.length > 0 &&
    state.passengers.length > 0 &&
    state.incidentType !== null;

  const incidentConfig = incidentTypes.find((t) => t.id === state.incidentType);

  return (
    <motion.div
      key="review"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="min-h-[70vh] py-8"
    >
      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onBack}
        className="fixed top-4 left-4 z-20 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-white/80 backdrop-blur-sm transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
      </motion.button>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-center mb-10"
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">
          Bereit zum Einreichen?
        </h1>
        <p className="text-lg text-slate-500">
          Überprüfen Sie Ihre Anspruchsdetails vor dem Einreichen
        </p>
      </motion.div>

      {/* Summary Grid */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Flights Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl border border-slate-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Plane className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Flüge</h3>
                <p className="text-sm text-slate-500">{state.editableSegments.length} Segment{state.editableSegments.length !== 1 ? 'e' : ''}</p>
              </div>
            </div>
            <button
              onClick={() => onEditSection('booking')}
              className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {state.editableSegments.filter(s => !s._isDeleted).slice(0, 3).map((seg) => (
              <div key={seg._id} className="flex items-center gap-2 text-sm">
                <span className="font-medium text-slate-700">{seg.carrier}{seg.flight_no}</span>
                <span className="text-slate-400">•</span>
                <span className="text-slate-600">{seg.dep_iata} → {seg.arr_iata}</span>
                <span className="text-slate-400">•</span>
                <span className="text-slate-500">{seg.dep_date}</span>
              </div>
            ))}
            {state.editableSegments.filter(s => !s._isDeleted).length > 3 && (
              <p className="text-xs text-slate-400">+{state.editableSegments.filter(s => !s._isDeleted).length - 3} weitere</p>
            )}
          </div>
        </motion.div>

        {/* Passengers Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-slate-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Passagiere</h3>
                <p className="text-sm text-slate-500">{state.passengers.length} Reisende{state.passengers.length !== 1 ? '' : 'r'}</p>
              </div>
            </div>
            <button
              onClick={() => onEditSection('booking')}
              className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {state.passengers.map((p) => (
              <span
                key={p._id}
                className="px-3 py-1 rounded-full bg-slate-100 text-sm text-slate-700"
              >
                {p.first_name} {p.last_name}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Incident Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl border border-slate-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                state.incidentType === 'delay' && 'bg-amber-100',
                state.incidentType === 'cancel' && 'bg-red-100',
                state.incidentType === 'denied_boarding' && 'bg-orange-100',
                (!state.incidentType || state.incidentType === 'other') && 'bg-slate-100'
              )}>
                <AlertTriangle className={cn(
                  'w-5 h-5',
                  state.incidentType === 'delay' && 'text-amber-600',
                  state.incidentType === 'cancel' && 'text-red-600',
                  state.incidentType === 'denied_boarding' && 'text-orange-600',
                  (!state.incidentType || state.incidentType === 'other') && 'text-slate-600'
                )} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Vorfall</h3>
                <p className="text-sm text-slate-500">{incidentConfig?.label || 'Nicht ausgewählt'}</p>
              </div>
            </div>
            <button
              onClick={() => onEditSection('incident')}
              className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
          {state.incidentDescription && (
            <p className="text-sm text-slate-600 line-clamp-2">{state.incidentDescription}</p>
          )}
        </motion.div>

        {/* Documents Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl border border-slate-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Dokumente</h3>
                <p className="text-sm text-slate-500">{state.documents.length} Datei{state.documents.length !== 1 ? 'en' : ''}</p>
              </div>
            </div>
            <button
              onClick={() => onEditSection('documentation')}
              className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
          {state.documents.length > 0 ? (
            <div className="space-y-1">
              {state.documents.slice(0, 3).map((doc) => (
                <p key={doc.id} className="text-sm text-slate-600 truncate">{doc.name}</p>
              ))}
              {state.documents.length > 3 && (
                <p className="text-xs text-slate-400">+{state.documents.length - 3} weitere</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Keine Dokumente hochgeladen</p>
          )}
        </motion.div>

        {/* Expenses Section */}
        {state.expenses.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-2xl border border-slate-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Ausgaben</h3>
                  <p className="text-sm text-slate-500">{state.expenses.length} Position{state.expenses.length !== 1 ? 'en' : ''}</p>
                </div>
              </div>
              <button
                onClick={() => onEditSection('expenses')}
                className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{state.expenses.length} Ausgabe{state.expenses.length !== 1 ? 'n' : ''}</span>
              <span className="text-lg font-semibold text-slate-900">
                {currencySymbol}{(expenseTotal / 100).toFixed(2)}
              </span>
            </div>
          </motion.div>
        )}

        {/* Narrative Section */}
        {state.narrative.trim() && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl border border-slate-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Ihre Geschichte</h3>
                  <p className="text-sm text-slate-500">{state.narrative.length} Zeichen</p>
                </div>
              </div>
              <button
                onClick={() => onEditSection('narrative')}
                className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-slate-600 line-clamp-3">{state.narrative}</p>
          </motion.div>
        )}
      </div>

      {/* Missing fields warning */}
      {!canSubmit && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="max-w-md mx-auto mt-8 p-4 rounded-xl bg-amber-50 border border-amber-200"
        >
          <p className="text-sm text-amber-700 font-medium mb-2">
            Bitte vervollständigen Sie die erforderlichen Felder:
          </p>
          <ul className="text-sm text-amber-600 space-y-1">
            {state.editableSegments.filter(s => !s._isDeleted).length === 0 && (
              <li>• Mindestens ein Flugsegment</li>
            )}
            {state.passengers.length === 0 && <li>• Mindestens ein Passagier</li>}
            {!state.incidentType && <li>• Was ist passiert (Vorfalltyp)</li>}
          </ul>
        </motion.div>
      )}

      {/* Submit button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex justify-center mt-10"
      >
        <AnimatedButton
          variant="primary"
          size="lg"
          onClick={onSubmit}
          disabled={!canSubmit}
          icon={<Send className="w-5 h-5" />}
          theme={theme}
          className="px-10 py-4 text-lg"
        >
          Anspruch einreichen
        </AnimatedButton>
      </motion.div>
    </motion.div>
  );
}
